import { Component, inject, input, output } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { AccessGroup, AccessGroupSearchEvent, DefaultAccessGroup } from 'src/app/features/access-groups/access-group.model';
import { LocationFacility } from '../../../charging-station-location-model';
import { MatInputModule } from '@angular/material/input';
import { MatFormField, MatOption, MatSelect, MatLabel, MatHint, MatSelectTrigger, MatSuffix } from '@angular/material/select';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { InputMaskModule } from '@ngneat/input-mask';
import { filter, Observable, of, switchMap } from 'rxjs';
import { PageEvent } from '@angular/material/paginator';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { ChargingStationLocationCreatedEditDialogService } from '../../charging-station-location-created-edit-dialog.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncPipe } from '@angular/common';
import { LocationFacilitiesSelectComponent } from 'src/app/shared/components/location-facilities-select/location-facilities-select.component';
import { MatIcon } from '@angular/material/icon';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { FeedbackInfoComponent } from 'ok-angular-shared/components';
import { AdmPricePolicyTypeaheadComponent } from 'src/app/shared/components/adm-price-policy-typeahead/adm-price-policy-typeahead.component';
import { PricePolicyType, PricePolicy } from 'src/app/features/prices/price-policies/interfaces/price-policy';

export interface Step3Form {
  selectedDefaultAccessGroups: FormControl<DefaultAccessGroup[]>;
  pricePolicyId: FormControl<number | null>;
  facilities: FormControl<LocationFacility[]>;
  isLargeVehicleAccessible: FormControl<boolean>;
}

@Component({
  selector: 'adm-csl-step3',
  imports: [
    MatFormField,
    MatInputModule,
    ReactiveFormsModule,
    MatOption,
    MatSelect,
    InputMaskModule,
    MatLabel,
    MatSelectTrigger,
    AsyncPipe,
    LocationFacilitiesSelectComponent,
    MatSlideToggle,
    MatHint,
    MatSuffix,
    MatIcon,
    CustomDropDownArrowDirective,
    AdmPricePolicyTypeaheadComponent,
    FeedbackInfoComponent,
  ],
  templateUrl: './step3.component.html',
  styleUrl: './step3.component.scss',
})
export class ChargingStationLocationEditDialogStep3Component {
  public accessGroups$: Observable<AccessGroup[]>;
  public accessGroupService = inject(AccessGroupService);
  public accessGroupSearchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(new PageEvent());
  service = inject(ChargingStationLocationCreatedEditDialogService);
  pricePolicyTypes: typeof PricePolicyType = PricePolicyType;

  form = input.required<FormGroup<Step3Form>>();
  organizationId = input<number | null>(null);
  canHaveDynamicPricePolicy = input<boolean>(true);
  hasOkPowerMeter = input<boolean>(false);
  pricePolicyChangeRequired = input<boolean>(false);
  netAreaRemoved = input<boolean>(false);
  okPowerMeterChangedFromYes = input<boolean>(false);
  pricePolicyResetTrigger = input<{ reset: boolean }>({ reset: false });
  pricePolicySelected = output<PricePolicy | null>();

  get selectedDefaultAccessGroups() {
    return this.form().controls.selectedDefaultAccessGroups;
  }

  constructor() {
    this.accessGroups$ = this.service.organizationChanged.pipe(
      filter((orgid): orgid is number => orgid != null),
      switchMap(orgid => {
        this.accessGroupSearchEvent.organizationId = orgid;
        return this.accessGroupService.getAccessGroups(this.accessGroupSearchEvent);
      }),
      switchMap(ag => {
        this.selectedDefaultAccessGroups.setValue([]);
        return of(ag.items);
      }),
      takeUntilDestroyed()
    );
  }

  compareObjects(ag1: DefaultAccessGroup, ag2: DefaultAccessGroup): boolean {
    return ag1?.identifier === ag2?.identifier;
  }

  onPricePolicySelected(pricePolicy: PricePolicy | null): void {
    this.form().controls.pricePolicyId.setValue(pricePolicy?.id ?? null);
    this.pricePolicySelected.emit(pricePolicy);
  }

  pricePolicyTypeByOkMeter(): PricePolicyType {
    return this.hasOkPowerMeter() ? this.pricePolicyTypes.FixedWithOkMeter : this.pricePolicyTypes.FixedWithoutOkMeter;
  }
}
